import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaGlobe,
  FaPlus,
  FaTrash,
  FaSave,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUpload,
  FaArrowLeft,
  FaArrowRight,
  FaQuoteLeft,
  FaUser,
  FaUndo,
} from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import WebinarRegistrationFormEditor from './WebinarRegistrationFormEditor';
import WebinarPageSectionsEditor from './WebinarPageSectionsEditor';

const ICON_OPTIONS = [
  { value: 'chart-line', label: 'Chart Line (FaChartLine)' },
  { value: 'handshake', label: 'Handshake (FaHandshake)' },
  { value: 'lightbulb', label: 'Lightbulb (FaLightbulb)' },
  { value: 'trophy', label: 'Trophy (FaTrophy)' },
];

const emptyHighlight = (index) => ({
  icon: 'chart-line',
  title: '',
  description: '',
  order: index,
});

const WEBINAR_CMS_SLUG = 'us-tax-return-webinar';
const MAIN_SITE_URL = process.env.REACT_APP_MAIN_SITE_URL || 'http://localhost:3002';

const normalizePublicSlug = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/^\/+|\/+$/g, '')
  .replace(/\s+/g, '-');

const toDatetimeLocalValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromDatetimeLocalValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString();
};

const ZohoEventCMS = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [selectedSlug, setSelectedSlug] = useState('zoho-books-ahmedabad');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    date: '',
    location: '',
    bannerImage: '',
    aboutTitle: 'About the Event',
    aboutText: '',
    highlights: [],
    gallery: [],
    testimonial: {
      quote: '',
      author: '',
    },
    speakers: [],
    whyAttendText: '',
    whatToExpect: [],
    countdownDate: '',
    heroImage: '',
    pageSections: {
      hero: { isActive: true, showRightImage: true, bgFrom: '', bgVia: '', bgTo: '' },
      countdown: { isActive: true, bgFrom: '', bgVia: '', bgTo: '' },
      whyAttend: { isActive: true, bgFrom: '', bgVia: '', bgTo: '' },
      speakers: { isActive: true, bgFrom: '', bgVia: '', bgTo: '' },
      registration: { isActive: true, bgFrom: '', bgVia: '', bgTo: '' },
    },
    eventDetailIcons: { date: '📅', time: '🕘', platform: '💻' },
    pageLabels: {
      heroBadge: '',
      ctaButton: '',
      timeIst: '',
      countdownLabel: '',
      speakersBadge: '',
      speakersTitle: '',
      speakersSubtitle: '',
      viewProfileLabel: '',
      speakerBadge: '',
      eventDetailDateLabel: '',
      eventDetailTimeLabel: '',
      eventDetailPlatformLabel: '',
      whatToExpectTitle: '',
    },
    registrationForm: {
      sectionTitle: '',
      sectionTitleHighlight: '',
      sectionSubtitle: '',
      submitLabel: '',
      submittingLabel: '',
      successTitle: '',
      successMessage: '',
      whatsappTitle: '',
      whatsappMessage: '',
      whatsappLink: '',
      whatsappButtonLabel: '',
      whatsappFooter: '',
      errorMessage: '',
      countryCodes: [],
      fields: [],
    },
    isPublished: true,
    publicSlug: 'us-tax-return-webinar',
    slugAliases: [],
  });

  const fetchContent = async (slug) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/events/admin/${slug}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const data = res.data.data;
        setForm({
          title: data.title || '',
          description: data.description || '',
          date: data.date || '',
          location: data.location || '',
          bannerImage: data.bannerImage || '',
          aboutTitle: data.aboutTitle || '',
          aboutText: data.aboutText || '',
          isPublished: data.isPublished !== false,
          publicSlug: data.publicSlug || data.slug || WEBINAR_CMS_SLUG,
          slugAliases: data.slugAliases || [],
          testimonial: {
            quote: data.testimonial?.quote || '',
            author: data.testimonial?.author || '',
          },
          highlights: (data.highlights || []).map((hl, index) => ({
            _id: hl._id,
            icon: hl.icon || 'chart-line',
            title: hl.title || '',
            description: hl.description || '',
            order: typeof hl.order === 'number' ? hl.order : index,
          })),
          gallery: data.gallery || [],
          speakers: (data.speakers || []).map((sp) => ({
            _id: sp._id,
            name: sp.name || '',
            role: sp.role || '',
            qualifications: sp.qualifications || '',
            image: sp.image || '',
            bio: sp.bio || [''],
            sessions: (sp.sessions || []).map((sess) => ({
              _id: sess._id,
              title: sess.title || '',
              date: sess.date || '',
              time: sess.time || '',
            })),
          })),
          whyAttendText: data.whyAttendText || '',
          whatToExpect: data.whatToExpect || [],
          countdownDate: toDatetimeLocalValue(data.countdownDate),
          heroImage: data.heroImage || '',
          pageSections: {
            hero: { isActive: data.pageSections?.hero?.isActive !== false, showRightImage: data.pageSections?.hero?.showRightImage !== false, bgFrom: data.pageSections?.hero?.bgFrom || '', bgVia: data.pageSections?.hero?.bgVia || '', bgTo: data.pageSections?.hero?.bgTo || '' },
            countdown: { isActive: data.pageSections?.countdown?.isActive !== false, bgFrom: data.pageSections?.countdown?.bgFrom || '', bgVia: data.pageSections?.countdown?.bgVia || '', bgTo: data.pageSections?.countdown?.bgTo || '' },
            whyAttend: { isActive: data.pageSections?.whyAttend?.isActive !== false, bgFrom: data.pageSections?.whyAttend?.bgFrom || '', bgVia: data.pageSections?.whyAttend?.bgVia || '', bgTo: data.pageSections?.whyAttend?.bgTo || '' },
            speakers: { isActive: data.pageSections?.speakers?.isActive !== false, bgFrom: data.pageSections?.speakers?.bgFrom || '', bgVia: data.pageSections?.speakers?.bgVia || '', bgTo: data.pageSections?.speakers?.bgTo || '' },
            registration: { isActive: data.pageSections?.registration?.isActive !== false, bgFrom: data.pageSections?.registration?.bgFrom || '', bgVia: data.pageSections?.registration?.bgVia || '', bgTo: data.pageSections?.registration?.bgTo || '' },
          },
          eventDetailIcons: {
            date: data.eventDetailIcons?.date || '📅',
            time: data.eventDetailIcons?.time || '🕘',
            platform: data.eventDetailIcons?.platform || '💻',
          },
          pageLabels: {
            heroBadge: data.pageLabels?.heroBadge ?? '',
            ctaButton: data.pageLabels?.ctaButton ?? '',
            timeIst: data.pageLabels?.timeIst ?? '',
            countdownLabel: data.pageLabels?.countdownLabel ?? '',
            speakersBadge: data.pageLabels?.speakersBadge ?? '',
            speakersTitle: data.pageLabels?.speakersTitle ?? '',
            speakersSubtitle: data.pageLabels?.speakersSubtitle ?? '',
            viewProfileLabel: data.pageLabels?.viewProfileLabel ?? '',
            speakerBadge: data.pageLabels?.speakerBadge ?? '',
            eventDetailDateLabel: data.pageLabels?.eventDetailDateLabel ?? '',
            eventDetailTimeLabel: data.pageLabels?.eventDetailTimeLabel ?? '',
            eventDetailPlatformLabel: data.pageLabels?.eventDetailPlatformLabel ?? '',
            whatToExpectTitle: data.pageLabels?.whatToExpectTitle ?? '',
          },
          registrationForm: {
            sectionTitle: data.registrationForm?.sectionTitle || '',
            sectionTitleHighlight: data.registrationForm?.sectionTitleHighlight || '',
            sectionSubtitle: data.registrationForm?.sectionSubtitle || '',
            submitLabel: data.registrationForm?.submitLabel || '',
            submittingLabel: data.registrationForm?.submittingLabel || '',
            successTitle: data.registrationForm?.successTitle || '',
            successMessage: data.registrationForm?.successMessage || '',
            whatsappTitle: data.registrationForm?.whatsappTitle || '',
            whatsappMessage: data.registrationForm?.whatsappMessage || '',
            whatsappLink: data.registrationForm?.whatsappLink || '',
            whatsappButtonLabel: data.registrationForm?.whatsappButtonLabel || '',
            whatsappFooter: data.registrationForm?.whatsappFooter || '',
            errorMessage: data.registrationForm?.errorMessage || '',
            countryCodes: data.registrationForm?.countryCodes || [],
            fields: (data.registrationForm?.fields || []).map((field, index) => ({
              key: field.key || `field_${index}`,
              label: field.label || '',
              type: field.type || 'text',
              required: !!field.required,
              placeholder: field.placeholder || '',
              order: typeof field.order === 'number' ? field.order : index,
              isActive: field.isActive !== false,
              options: field.options || [],
            })),
          },
        });
      }
    } catch (err) {
      toastError(`Failed to load event CMS content for ${slug}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent(selectedSlug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlug]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (parent, field, value) => {
    setForm((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));
  };

  const updateHighlight = (index, field, value) => {
    setForm((prev) => {
      const highlights = [...prev.highlights];
      highlights[index] = { ...highlights[index], [field]: value };
      return { ...prev, highlights };
    });
  };

  const addHighlight = () => {
    setForm((prev) => ({
      ...prev,
      highlights: [...prev.highlights, emptyHighlight(prev.highlights.length)],
    }));
  };

  const removeHighlight = (index) => {
    if (!window.confirm('Remove this highlight?')) return;
    setForm((prev) => ({
      ...prev,
      highlights: prev.highlights
        .filter((_, i) => i !== index)
        .map((hl, i) => ({ ...hl, order: i })),
    }));
  };

  const moveHighlight = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= form.highlights.length) return;
    setForm((prev) => {
      const highlights = [...prev.highlights];
      [highlights[index], highlights[target]] = [highlights[target], highlights[index]];
      return {
        ...prev,
        highlights: highlights.map((hl, i) => ({ ...hl, order: i })),
      };
    });
  };

  // Speakers Panel Actions
  const addSpeaker = () => {
    setForm((prev) => ({
      ...prev,
      speakers: [
        ...prev.speakers,
        {
          name: '',
          role: '',
          qualifications: '',
          image: '',
          bio: [''],
          sessions: [{ title: '', date: '', time: '' }],
        },
      ],
    }));
  };

  const removeSpeaker = (index) => {
    if (!window.confirm('Delete this speaker?')) return;
    setForm((prev) => ({
      ...prev,
      speakers: prev.speakers.filter((_, i) => i !== index),
    }));
  };

  const updateSpeaker = (index, field, value) => {
    setForm((prev) => {
      const speakers = [...prev.speakers];
      speakers[index] = { ...speakers[index], [field]: value };
      return { ...prev, speakers };
    });
  };

  const addSpeakerBioParagraph = (spIndex) => {
    setForm((prev) => {
      const speakers = [...prev.speakers];
      speakers[spIndex].bio = [...speakers[spIndex].bio, ''];
      return { ...prev, speakers };
    });
  };

  const updateSpeakerBioParagraph = (spIndex, bioIndex, value) => {
    setForm((prev) => {
      const speakers = [...prev.speakers];
      const bio = [...speakers[spIndex].bio];
      bio[bioIndex] = value;
      speakers[spIndex].bio = bio;
      return { ...prev, speakers };
    });
  };

  const removeSpeakerBioParagraph = (spIndex, bioIndex) => {
    setForm((prev) => {
      const speakers = [...prev.speakers];
      speakers[spIndex].bio = speakers[spIndex].bio.filter((_, i) => i !== bioIndex);
      return { ...prev, speakers };
    });
  };

  const addSpeakerSession = (spIndex) => {
    setForm((prev) => {
      const speakers = [...prev.speakers];
      speakers[spIndex].sessions = [
        ...speakers[spIndex].sessions,
        { title: '', date: '', time: '' },
      ];
      return { ...prev, speakers };
    });
  };

  const updateSpeakerSession = (spIndex, sessIndex, field, value) => {
    setForm((prev) => {
      const speakers = [...prev.speakers];
      const sessions = [...speakers[spIndex].sessions];
      sessions[sessIndex] = { ...sessions[sessIndex], [field]: value };
      speakers[spIndex].sessions = sessions;
      return { ...prev, speakers };
    });
  };

  const removeSpeakerSession = (spIndex, sessIndex) => {
    setForm((prev) => {
      const speakers = [...prev.speakers];
      speakers[spIndex].sessions = speakers[spIndex].sessions.filter((_, i) => i !== sessIndex);
      return { ...prev, speakers };
    });
  };

  const handleSpeakerImageUpload = async (e, spIndex) => {
    const file = e.target.files[0];
    if (!file) return;

    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/events/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      if (res.data.success) {
        updateSpeaker(spIndex, 'image', res.data.url);
        toastSuccess('Speaker image uploaded successfully');
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to upload speaker image');
    }
  };

  // What to Expect Actions
  const addExpectation = () => {
    setForm((prev) => ({
      ...prev,
      whatToExpect: [...prev.whatToExpect, ''],
    }));
  };

  const updateExpectation = (index, value) => {
    setForm((prev) => {
      const whatToExpect = [...prev.whatToExpect];
      whatToExpect[index] = value;
      return { ...prev, whatToExpect };
    });
  };

  const removeExpectation = (index) => {
    setForm((prev) => ({
      ...prev,
      whatToExpect: prev.whatToExpect.filter((_, i) => i !== index),
    }));
  };

  // Banner image upload
  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingBanner(true);
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/events/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      if (res.data.success) {
        updateField('bannerImage', res.data.url);
        toastSuccess('Banner image uploaded successfully');
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to upload banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingHeroImage(true);
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/events/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      if (res.data.success) {
        updateField('heroImage', res.data.url);
        toastSuccess('Hero image uploaded successfully');
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to upload hero image');
    } finally {
      setUploadingHeroImage(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingGallery(true);
    const token = localStorage.getItem('adminToken');
    const newImages = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('image', files[i]);
        const res = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/cms/events/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        if (res.data.success) {
          newImages.push(res.data.url);
        }
      }

      setForm((prev) => ({
        ...prev,
        gallery: [...prev.gallery, ...newImages],
      }));
      toastSuccess(`Uploaded ${newImages.length} image(s) to gallery`);
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to upload gallery images');
    } finally {
      setUploadingGallery(false);
    }
  };

  const moveGalleryImage = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= form.gallery.length) return;
    setForm((prev) => {
      const gallery = [...prev.gallery];
      [gallery[index], gallery[target]] = [gallery[target], gallery[index]];
      return { ...prev, gallery };
    });
  };

  const removeGalleryImage = (index) => {
    if (!window.confirm('Delete this photo from the gallery?')) return;
    setForm((prev) => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const payload = selectedSlug === WEBINAR_CMS_SLUG
        ? {
            ...form,
            publicSlug: normalizePublicSlug(form.publicSlug),
            countdownDate: fromDatetimeLocalValue(form.countdownDate),
            pageLabels: {
              ...form.pageLabels,
              timeEst: '',
            },
          }
        : form;
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/events/${selectedSlug}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Event content saved successfully');
        fetchContent(selectedSlug);
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save event content');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore preloaded event content? This will overwrite your edits including images and speakers.')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/events/admin/${selectedSlug}/reload-defaults`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Preloaded defaults restored');
        fetchContent(selectedSlug);
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reload defaults');
    } finally {
      setResetting(false);
    }
  };

  const resolveImage = (path) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    if (path.startsWith('/images/')) return `${process.env.REACT_APP_API_BASE_URL.replace(/\/api$/, '')}${path}`;
    return `${process.env.REACT_APP_API_BASE_URL.replace('/api', '')}/${path}`;
  };

  return (
    <PageLayout>
      <PageHeader
        title="Event Landing Pages CMS"
        description="Manage the details, sections, speakers, and layout configurations of event pages"
        icon={FaGlobe}
        actions={
          <div className="flex items-center gap-3">
            <select
              value={selectedSlug}
              onChange={(e) => setSelectedSlug(e.target.value)}
              className="px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-sm font-semibold shadow-sm focus:outline-none"
            >
              <option value="zoho-books-ahmedabad">Zoho Ahmedabad Event</option>
              <option value={WEBINAR_CMS_SLUG}>US Tax Return Webinar</option>
            </select>
            <button
              onClick={handleReset}
              disabled={resetting || saving || loading}
              className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors flex items-center gap-2"
            >
              <FaUndo className="w-3.5 h-3.5" /> Reload Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="btn-premium flex items-center gap-2"
            >
              <FaSave className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        }
      />

      {loading ? (
        <SkeletonLoader variant="page" />
      ) : (
        <div className="space-y-6">
          {/* Header & Settings Panel */}
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Hero & Header Settings</h2>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  onChange={(e) => updateField('isPublished', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Published
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Event Title</label>
                <input
                  className="input-modern"
                  value={form.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder="Event page title"
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Subtitle / Description
                  {selectedSlug === WEBINAR_CMS_SLUG && (
                    <span className="normal-case font-normal text-gray-400 ml-1">— shown as subtitle below the title</span>
                  )}
                </label>
                {selectedSlug === WEBINAR_CMS_SLUG ? (
                  <textarea
                    className="input-modern min-h-[100px]"
                    value={form.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="Large subtitle shown below the event title"
                  />
                ) : (
                  <input
                    className="input-modern"
                    value={form.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="Event subtitle or summary"
                  />
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Date Text</label>
                <div className="relative">
                  <FaCalendarAlt className="absolute left-3.5 top-3.5 text-gray-400" />
                  <input
                    className="input-modern pl-10"
                    value={form.date}
                    onChange={(e) => updateField('date', e.target.value)}
                    placeholder="e.g. Saturday, 14th March 2026"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Location / Platform</label>
                <div className="relative">
                  <FaMapMarkerAlt className="absolute left-3.5 top-3.5 text-gray-400" />
                  <input
                    className="input-modern pl-10"
                    value={form.location}
                    onChange={(e) => updateField('location', e.target.value)}
                    placeholder="e.g. Online Webinar (Link will be shared)"
                  />
                </div>
              </div>
            </div>

            {selectedSlug === WEBINAR_CMS_SLUG && (
              <>
              <div className="rounded-2xl border border-primary-100 dark:border-primary-900/40 bg-primary-50/40 dark:bg-primary-900/10 p-4 space-y-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Page URL Slug</label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex items-center flex-1 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 overflow-hidden">
                      <span className="px-3 py-2.5 text-sm text-gray-500 border-r border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-900/50">/</span>
                      <input
                        className="flex-1 px-3 py-2.5 text-sm bg-transparent outline-none"
                        value={form.publicSlug}
                        onChange={(e) => updateField('publicSlug', normalizePublicSlug(e.target.value))}
                        placeholder="us-tax-return-webinar"
                      />
                    </div>
                    <a
                      href={`${MAIN_SITE_URL}/${normalizePublicSlug(form.publicSlug || WEBINAR_CMS_SLUG)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300 text-sm font-semibold hover:bg-primary-50 dark:hover:bg-primary-900/20"
                    >
                      Open Page
                    </a>
                  </div>
                  <p className="text-xs text-gray-500">
                    Public page URL: <span className="font-semibold text-primary-600">{MAIN_SITE_URL}/{normalizePublicSlug(form.publicSlug || WEBINAR_CMS_SLUG)}</span>
                  </p>
                  <p className="text-xs text-gray-400">Use lowercase letters, numbers, and hyphens only. Previous URLs keep working after you change this.</p>
                </div>
                {form.slugAliases?.length > 0 && (
                  <div className="text-xs text-gray-500">
                    <span className="font-semibold uppercase tracking-wider">Previous URLs:</span>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {form.slugAliases.map((alias) => (
                        <span key={alias} className="px-2 py-1 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700">
                          /{alias}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2 border-t border-gray-150 dark:border-dark-750">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Badge</label>
                  <input className="input-modern" value={form.pageLabels.heroBadge} onChange={(e) => updateNestedField('pageLabels', 'heroBadge', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">CTA Button Text</label>
                  <input className="input-modern" value={form.pageLabels.ctaButton} onChange={(e) => updateNestedField('pageLabels', 'ctaButton', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Time (IST)</label>
                  <input className="input-modern" value={form.pageLabels.timeIst} onChange={(e) => updateNestedField('pageLabels', 'timeIst', e.target.value)} placeholder="e.g. IST: 9:00 PM" />
                </div>
              </div>

              <div className="rounded-2xl border border-amber-100 dark:border-amber-900/40 bg-amber-50/40 dark:bg-amber-900/10 p-4 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200">Countdown Timer</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Shown below the hero when enabled and a target date is set.</p>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.pageSections?.countdown?.isActive !== false}
                      onChange={(e) => updateNestedField('pageSections', 'countdown', {
                        ...(form.pageSections?.countdown || {}),
                        isActive: e.target.checked,
                      })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Show on website
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Countdown Label</label>
                    <input
                      className="input-modern"
                      value={form.pageLabels.countdownLabel}
                      onChange={(e) => updateNestedField('pageLabels', 'countdownLabel', e.target.value)}
                      placeholder="Event Starts In"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Countdown Target Date & Time</label>
                    <input
                      type="datetime-local"
                      className="input-modern"
                      value={form.countdownDate}
                      onChange={(e) => updateField('countdownDate', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-2 border-t border-gray-150 dark:border-dark-750">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 block">Hero Right Side Image</label>
                  <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.pageSections?.hero?.showRightImage !== false}
                      onChange={(e) => updateNestedField('pageSections', 'hero', {
                        ...(form.pageSections?.hero || {}),
                        showRightImage: e.target.checked,
                      })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Show on website
                  </label>
                </div>
                <p className="text-xs text-gray-400">
                  {form.pageSections?.hero?.showRightImage !== false
                    ? 'Upload a custom image, or leave empty to show the default webinar graphic.'
                    : 'Right side image is hidden on the public page. Hero text will use full width.'}
                </p>
                {form.pageSections?.hero?.showRightImage !== false && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                  {form.heroImage ? (
                    <div className="relative col-span-2 rounded-2xl overflow-hidden border border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-900 max-h-[220px]">
                      <img
                        src={resolveImage(form.heroImage)}
                        alt="Hero preview"
                        className="w-full h-full object-cover max-h-[220px]"
                      />
                      <button
                        type="button"
                        onClick={() => updateField('heroImage', '')}
                        className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-lg p-2 transition-colors shadow-lg"
                      >
                        <FaTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="col-span-2 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-dark-600 rounded-2xl p-6 text-center text-gray-500">
                      <FaUpload className="w-8 h-8 mb-2 text-gray-400" />
                      <p className="text-sm font-semibold">No Hero Image — default graphic will show</p>
                    </div>
                  )}
                  <div className="flex flex-col gap-3">
                    <input
                      type="file"
                      id="hero-image-upload-header"
                      accept="image/*"
                      onChange={handleHeroImageUpload}
                      className="hidden"
                    />
                    <label
                      htmlFor="hero-image-upload-header"
                      className={`btn-premium flex items-center justify-center gap-2 cursor-pointer text-center ${uploadingHeroImage ? 'opacity-60 pointer-events-none' : ''}`}
                    >
                      <FaUpload />
                      {uploadingHeroImage ? 'Uploading...' : 'Upload Hero Image'}
                    </label>
                    <input
                      className="input-modern text-xs"
                      value={form.heroImage}
                      onChange={(e) => updateField('heroImage', e.target.value)}
                      placeholder="Image URL or path"
                    />
                  </div>
                </div>
                )}
              </div>
              </>
            )}

            {/* Banner Image - only for Zoho Events */}
            {selectedSlug === 'zoho-books-ahmedabad' && (
              <div className="space-y-3 pt-2 border-t border-gray-150 dark:border-dark-750">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 block">Banner / Main Event Image</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                  {form.bannerImage ? (
                    <div className="relative col-span-2 rounded-2xl overflow-hidden border border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-900 max-h-[220px]">
                      <img
                        src={resolveImage(form.bannerImage)}
                        alt="Banner Preview"
                        className="w-full h-full object-cover max-h-[220px]"
                      />
                      <button
                        type="button"
                        onClick={() => updateField('bannerImage', '')}
                        className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-lg p-2 transition-colors shadow-lg"
                      >
                        <FaTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="col-span-2 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-dark-600 rounded-2xl p-6 text-center text-gray-500">
                      <FaUpload className="w-8 h-8 mb-2 text-gray-400" />
                      <p className="text-sm font-semibold">No Banner Image Selected</p>
                    </div>
                  )}
                  <div className="flex flex-col gap-3">
                    <input
                      type="file"
                      id="banner-upload"
                      accept="image/*"
                      onChange={handleBannerUpload}
                      className="hidden"
                    />
                    <label
                      htmlFor="banner-upload"
                      className={`btn-premium flex items-center justify-center gap-2 cursor-pointer text-center ${uploadingBanner ? 'opacity-60 pointer-events-none' : ''}`}
                    >
                      <FaUpload />
                      {uploadingBanner ? 'Uploading...' : 'Upload Image'}
                    </label>
                    <input
                      className="input-modern text-xs"
                      value={form.bannerImage}
                      onChange={(e) => updateField('bannerImage', e.target.value)}
                      placeholder="/images/events/Images1.jpeg"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* About Section */}
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-dark-700 pb-4">
              {selectedSlug === WEBINAR_CMS_SLUG ? 'Why Attend Section' : 'About Section'}
            </h2>
            <div className="grid grid-cols-1 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Title</label>
                <input
                  className="input-modern"
                  value={form.aboutTitle}
                  onChange={(e) => updateField('aboutTitle', e.target.value)}
                  placeholder="e.g. About the Event / Why Attend"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Description / Text</label>
                <textarea
                  className="input-modern min-h-[120px]"
                  value={form.aboutText}
                  onChange={(e) => updateField('aboutText', e.target.value)}
                  placeholder="Main section copy..."
                />
              </div>
              {selectedSlug === WEBINAR_CMS_SLUG && (
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Closing Footer Text</label>
                  <input
                    className="input-modern"
                    value={form.whyAttendText}
                    onChange={(e) => updateField('whyAttendText', e.target.value)}
                    placeholder="Don't miss this opportunity to simplify your US tax filing journey."
                  />
                </div>
              )}
            </div>
          </div>

          {/* Conditional Sections based on Slug */}
          {selectedSlug === 'zoho-books-ahmedabad' ? (
            <>
              {/* Highlights Section */}
              <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
                <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Key Highlights</h2>
                  <button
                    onClick={addHighlight}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-xl transition-colors"
                  >
                    <FaPlus className="w-3.5 h-3.5" /> Add Highlight
                  </button>
                </div>

                {form.highlights.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">No highlights yet.</p>
                ) : (
                  <div className="space-y-5">
                    {form.highlights.map((hl, index) => (
                      <div
                        key={hl._id || `hl-${index}`}
                        className="rounded-2xl border border-gray-200 dark:border-dark-600 bg-gray-50/50 dark:bg-dark-900/30 p-5 space-y-4"
                      >
                        <div className="flex items-center justify-between gap-3 border-b border-gray-100 dark:border-dark-700/50 pb-2">
                          <span className="text-sm font-bold text-gray-700 dark:text-gray-200">Highlight {index + 1}</span>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => moveHighlight(index, -1)}
                              disabled={index === 0}
                              className="px-2 py-1 text-xs rounded border disabled:opacity-40"
                            >
                              Up
                            </button>
                            <button
                              type="button"
                              onClick={() => moveHighlight(index, 1)}
                              disabled={index === form.highlights.length - 1}
                              className="px-2 py-1 text-xs rounded border disabled:opacity-40"
                            >
                              Down
                            </button>
                            <button
                              type="button"
                              onClick={() => removeHighlight(index)}
                              className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase">Icon</label>
                            <select
                              className="input-modern mt-1"
                              value={hl.icon}
                              onChange={(e) => updateHighlight(index, 'icon', e.target.value)}
                            >
                              {ICON_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="md:col-span-2">
                            <label className="text-xs font-semibold text-gray-500 uppercase">Title</label>
                            <input
                              className="input-modern mt-1"
                              value={hl.title}
                              onChange={(e) => updateHighlight(index, 'title', e.target.value)}
                            />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <label className="text-xs font-semibold text-gray-500 uppercase">Description</label>
                          <textarea
                            className="input-modern min-h-[60px]"
                            value={hl.description}
                            onChange={(e) => updateHighlight(index, 'description', e.target.value)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Gallery Section */}
              <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
                <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Event Gallery</h2>
                    <p className="text-xs text-gray-500">Upload and order photos</p>
                  </div>
                  <div>
                    <input
                      type="file"
                      id="gallery-upload"
                      multiple
                      accept="image/*"
                      onChange={handleGalleryUpload}
                      className="hidden"
                    />
                    <label
                      htmlFor="gallery-upload"
                      className={`btn-premium flex items-center gap-2 cursor-pointer text-sm font-semibold ${uploadingGallery ? 'opacity-60 pointer-events-none' : ''}`}
                    >
                      <FaUpload className="w-3.5 h-3.5" />
                      {uploadingGallery ? 'Uploading...' : 'Upload Photos'}
                    </label>
                  </div>
                </div>

                {form.gallery.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">No gallery photos.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {form.gallery.map((imgUrl, index) => (
                      <div
                        key={index}
                        className="group relative rounded-2xl overflow-hidden border border-gray-200 dark:border-dark-700 aspect-video flex flex-col justify-between"
                      >
                        <img
                          src={resolveImage(imgUrl)}
                          alt=""
                          className="w-full h-[80px] object-cover"
                        />
                        <div className="flex justify-between p-1 bg-gray-50 dark:bg-dark-900">
                          <span className="text-[10px] px-1 font-semibold text-gray-500">#{index+1}</span>
                          <div className="flex gap-0.5">
                            <button
                              type="button"
                              onClick={() => moveGalleryImage(index, -1)}
                              disabled={index === 0}
                              className="p-1 rounded bg-white dark:bg-dark-800 disabled:opacity-30"
                            >
                              <FaArrowLeft className="w-2.5 h-2.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveGalleryImage(index, 1)}
                              disabled={index === form.gallery.length - 1}
                              className="p-1 rounded bg-white dark:bg-dark-800 disabled:opacity-30"
                            >
                              <FaArrowRight className="w-2.5 h-2.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeGalleryImage(index)}
                              className="p-1 rounded bg-red-50 text-red-500"
                            >
                              <FaTrash className="w-2.5 h-2.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Testimonial Section */}
              <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-dark-700 pb-4">
                  Testimonial
                </h2>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Quote</label>
                    <div className="relative">
                      <FaQuoteLeft className="absolute left-3.5 top-3.5 text-gray-300 dark:text-dark-600" />
                      <textarea
                        className="input-modern pl-10 min-h-[80px]"
                        value={form.testimonial.quote}
                        onChange={(e) => updateNestedField('testimonial', 'quote', e.target.value)}
                        placeholder="Informative and engaging session..."
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Author Name</label>
                    <input
                      className="input-modern"
                      value={form.testimonial.author}
                      onChange={(e) => updateNestedField('testimonial', 'author', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-dark-700 pb-4">
                  Page Sections & Styling
                </h2>
                <WebinarPageSectionsEditor form={form} setForm={setForm} />
              </div>

              {/* US Tax Webinar Expectations List */}
              <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
                <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">What to Expect Bullets</h2>
                  <button
                    onClick={addExpectation}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-xl transition-colors"
                  >
                    <FaPlus className="w-3.5 h-3.5" /> Add Point
                  </button>
                </div>

                {form.whatToExpect.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-6">No bullet points yet.</p>
                ) : (
                  <div className="space-y-3">
                    {form.whatToExpect.map((pt, index) => (
                      <div key={index} className="flex gap-3 items-center">
                        <span className="text-sm font-bold text-gray-400 w-6">#{index + 1}</span>
                        <input
                          className="input-modern flex-1"
                          value={pt}
                          onChange={(e) => updateExpectation(index, e.target.value)}
                          placeholder="e.g. Comprehensive overview of US tax return 2025 key changes"
                        />
                        <button
                          type="button"
                          onClick={() => removeExpectation(index)}
                          className="p-3 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl transition-colors"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Speakers Manager Panel */}
              <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
                <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Page Labels & Speakers</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Speakers badge</label>
                    <input className="input-modern mt-1" value={form.pageLabels.speakersBadge} onChange={(e) => updateNestedField('pageLabels', 'speakersBadge', e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Speakers title</label>
                    <input className="input-modern mt-1" value={form.pageLabels.speakersTitle} onChange={(e) => updateNestedField('pageLabels', 'speakersTitle', e.target.value)} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-semibold uppercase text-gray-500">Speakers subtitle</label>
                    <input className="input-modern mt-1" value={form.pageLabels.speakersSubtitle} onChange={(e) => updateNestedField('pageLabels', 'speakersSubtitle', e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">View profile label</label>
                    <input className="input-modern mt-1" value={form.pageLabels.viewProfileLabel} onChange={(e) => updateNestedField('pageLabels', 'viewProfileLabel', e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">What to expect title</label>
                    <input className="input-modern mt-1" value={form.pageLabels.whatToExpectTitle} onChange={(e) => updateNestedField('pageLabels', 'whatToExpectTitle', e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Speaker badge</label>
                    <input className="input-modern mt-1" value={form.pageLabels.speakerBadge} onChange={(e) => updateNestedField('pageLabels', 'speakerBadge', e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Event detail — Date label</label>
                    <input className="input-modern mt-1" value={form.pageLabels.eventDetailDateLabel} onChange={(e) => updateNestedField('pageLabels', 'eventDetailDateLabel', e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Event detail — Time label</label>
                    <input className="input-modern mt-1" value={form.pageLabels.eventDetailTimeLabel} onChange={(e) => updateNestedField('pageLabels', 'eventDetailTimeLabel', e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Event detail — Platform label</label>
                    <input className="input-modern mt-1" value={form.pageLabels.eventDetailPlatformLabel} onChange={(e) => updateNestedField('pageLabels', 'eventDetailPlatformLabel', e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-dark-700 pb-4">
                  Registration Form
                </h2>
                <WebinarRegistrationFormEditor form={form} setForm={setForm} />
              </div>

              <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
                <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Webinar Speakers</h2>
                  <button
                    onClick={addSpeaker}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-xl transition-colors"
                  >
                    <FaPlus className="w-3.5 h-3.5" /> Add Speaker
                  </button>
                </div>

                {form.speakers.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">No speakers added yet.</p>
                ) : (
                  <div className="space-y-8">
                    {form.speakers.map((sp, spIndex) => (
                      <div
                        key={sp._id || `sp-${spIndex}`}
                        className="rounded-2xl border border-gray-250 dark:border-dark-650 bg-gray-50/30 dark:bg-dark-900/10 p-6 space-y-4"
                      >
                        <div className="flex items-center justify-between border-b border-gray-150 dark:border-dark-750 pb-2">
                          <span className="text-base font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2">
                            <FaUser className="text-primary-500" /> Speaker #{spIndex + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeSpeaker(spIndex)}
                            className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                          >
                            <FaTrash />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <label className="text-xs font-semibold uppercase text-gray-500">Name</label>
                            <input
                              className="input-modern"
                              value={sp.name}
                              onChange={(e) => updateSpeaker(spIndex, 'name', e.target.value)}
                              placeholder="CA Chintan Patel"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-semibold uppercase text-gray-500">Role / Designation</label>
                            <input
                              className="input-modern"
                              value={sp.role}
                              onChange={(e) => updateSpeaker(spIndex, 'role', e.target.value)}
                              placeholder="Partner / Speaker"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-semibold uppercase text-gray-500">Qualifications</label>
                            <input
                              className="input-modern"
                              value={sp.qualifications}
                              onChange={(e) => updateSpeaker(spIndex, 'qualifications', e.target.value)}
                              placeholder="CA, CPA (USA), CISA(USA)"
                            />
                          </div>
                          {/* Image upload */}
                          <div className="space-y-1">
                            <label className="text-xs font-semibold uppercase text-gray-500 block">Speaker Photo URL</label>
                            <div className="flex gap-2">
                              <input
                                className="input-modern text-xs"
                                value={sp.image}
                                onChange={(e) => updateSpeaker(spIndex, 'image', e.target.value)}
                                placeholder="Image URL / Upload file"
                              />
                              <input
                                type="file"
                                id={`sp-img-${spIndex}`}
                                accept="image/*"
                                onChange={(e) => handleSpeakerImageUpload(e, spIndex)}
                                className="hidden"
                              />
                              <label
                                htmlFor={`sp-img-${spIndex}`}
                                className="px-4 py-2 border rounded-xl hover:bg-gray-100 dark:hover:bg-dark-800 cursor-pointer flex items-center justify-center"
                              >
                                <FaUpload />
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Speaker Bio Paragraphs */}
                        <div className="space-y-2 border-t border-gray-150 dark:border-dark-750 pt-3">
                          <div className="flex justify-between items-center">
                            <label className="text-xs font-bold uppercase text-gray-500">Bio Paragraphs</label>
                            <button
                              type="button"
                              onClick={() => addSpeakerBioParagraph(spIndex)}
                              className="text-xs text-primary-600 font-semibold flex items-center gap-1"
                            >
                              <FaPlus /> Paragraph
                            </button>
                          </div>
                          {sp.bio.length === 0 ? (
                            <p className="text-xs text-gray-400">No bio added yet.</p>
                          ) : (
                            <div className="space-y-2">
                              {sp.bio.map((p, pIndex) => (
                                <div key={pIndex} className="flex gap-2 items-center">
                                  <input
                                    className="input-modern text-sm"
                                    value={p}
                                    onChange={(e) => updateSpeakerBioParagraph(spIndex, pIndex, e.target.value)}
                                    placeholder={`Paragraph ${pIndex + 1}`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeSpeakerBioParagraph(spIndex, pIndex)}
                                    className="p-2 text-red-400 hover:text-red-650"
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Speaker Sessions list */}
                        <div className="space-y-2 border-t border-gray-150 dark:border-dark-750 pt-3">
                          <div className="flex justify-between items-center">
                            <label className="text-xs font-bold uppercase text-gray-500">Sessions</label>
                            <button
                              type="button"
                              onClick={() => addSpeakerSession(spIndex)}
                              className="text-xs text-primary-600 font-semibold flex items-center gap-1"
                            >
                              <FaPlus /> Session
                            </button>
                          </div>
                          {sp.sessions.length === 0 ? (
                            <p className="text-xs text-gray-400">No sessions configured.</p>
                          ) : (
                            <div className="space-y-3">
                              {sp.sessions.map((sess, sessIndex) => (
                                <div key={sessIndex} className="border border-gray-200 dark:border-dark-700 rounded-xl p-3 space-y-2">
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs font-bold text-gray-500">Session #{sessIndex + 1}</span>
                                    <button
                                      type="button"
                                      onClick={() => removeSpeakerSession(spIndex, sessIndex)}
                                      className="text-xs text-red-500"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <input
                                      className="input-modern text-xs"
                                      value={sess.title}
                                      onChange={(e) => updateSpeakerSession(spIndex, sessIndex, 'title', e.target.value)}
                                      placeholder="Session Title (e.g. Incorporating Business in USA)"
                                    />
                                    <input
                                      className="input-modern text-xs"
                                      value={sess.date}
                                      onChange={(e) => updateSpeakerSession(spIndex, sessIndex, 'date', e.target.value)}
                                      placeholder="Session Date (e.g. Day - 1 : Wed, Sep 03, 2025)"
                                    />
                                    <input
                                      className="input-modern text-xs"
                                      value={sess.time}
                                      onChange={(e) => updateSpeakerSession(spIndex, sessIndex, 'time', e.target.value)}
                                      placeholder="Session Time (e.g. 05:15 PM - 06:15 PM IST)"
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </PageLayout>
  );
};

export default ZohoEventCMS;
